import { useEffect, useRef } from 'react'
import { useThree, useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import useGame from '../stores/useGame.jsx'
import { getCameraPreset, getSecEntry, resolveCamera } from '../config/cameraPresets.js'

const BASE_FOV = 50
const TABLET_FOV = 60
const TABLET_PORTRAIT_FOV = 90
const MOBILE_LANDSCAPE_FOV = 60
const MOBILE_PORTRAIT_FOV = 90

const TABLET_PORTRAIT   = '(min-width: 768px) and (max-width: 1024px) and (orientation: portrait)'
const TABLET_LANDSCAPE  = '(min-width: 900px) and (max-width: 1366px) and (orientation: landscape)'
const MOBILE_PORTRAIT   = '(max-width: 767px) and (orientation: portrait)'
const MOBILE_LANDSCAPE  = '(max-width: 899px) and (orientation: landscape)'

// SEC006 camera X offset — nudge left per device tier (set to 0 to disable)
const SEC006_X_OFFSET_TABLET_PORTRAIT = 1.35
const SEC006_X_OFFSET_MOBILE_PORTRAIT = 1.4

function useFOV() {
    const { camera } = useThree()

    useEffect(() => {
        const mql = [
            window.matchMedia(TABLET_PORTRAIT),
            window.matchMedia(TABLET_LANDSCAPE),
            window.matchMedia(MOBILE_PORTRAIT),
            window.matchMedia(MOBILE_LANDSCAPE),
        ]

        const update = () => {
            const isTabletPortrait   = mql[0].matches
            const isTabletLandscape  = mql[1].matches
            const isMobilePortrait   = mql[2].matches
            if (isMobilePortrait)       camera.fov = MOBILE_PORTRAIT_FOV
            else if (mql[3].matches)    camera.fov = MOBILE_LANDSCAPE_FOV
            else if (isTabletPortrait)  camera.fov = TABLET_PORTRAIT_FOV
            else if (isTabletLandscape) camera.fov = TABLET_FOV
            else                        camera.fov = BASE_FOV
            camera.updateProjectionMatrix()
        }

        update()
        mql.forEach(m => m.addEventListener('change', update))
        return () => mql.forEach(m => m.removeEventListener('change', update))
    }, [camera])
}

export function CameraAnimator({ cameraControlsRef }) {
    const { scene } = useThree()
    useFOV()
    const defaultCameraPos = useGame((state) => state.defaultCameraPos)
    const defaultCameraTar = useGame((state) => state.defaultCameraTar)
    const cinematicCameraPos = useGame((state) => state.cinematicCameraPos)
    const cinematicCameraTar = useGame((state) => state.cinematicCameraTar)
    const deviceType = useGame((state) => state.deviceType)
    const deviceOrientation = useGame((state) => state.deviceOrientation)
    // Store default position/target
    const initialPositionRef = useRef(new THREE.Vector3())
    const initialTargetRef = useRef(new THREE.Vector3())
    const cinematicPositionRef = useRef(new THREE.Vector3())
    const cinematicTargetRef = useRef(new THREE.Vector3())
    
    // Animation state for manual interpolation
    const animatingRef = useRef(false)
    const startPosRef = useRef(new THREE.Vector3())
    const startTarRef = useRef(new THREE.Vector3())
    const targetPosRef = useRef(new THREE.Vector3())
    const targetTarRef = useRef(new THREE.Vector3())
    const progressRef = useRef(0)
    const durationRef = useRef(2.0) // 2 seconds for transition
    // Set true during the inbound cinematic camera move so useFrame can dispatch
    // 'cinematic-camera-ready' the exact frame the camera reaches its target.
    const cinematicInboundRef = useRef(false)

    // Helper: resolve a preset entry into world-space Vectors via resolveCamera
    const resolve = (entry) => resolveCamera(scene, entry)

    // Shared: compute shortest-path spherical arc from current camera to
    // (posWorld, tarWorld) and kick off the manual animation.
    const animateToTarget = (posWorld, tarWorld) => {
        if (!cameraControlsRef.current) return
        const controls = cameraControlsRef.current
        const currentCamPos = controls.getPosition(new THREE.Vector3())
        const currentTar = controls.getTarget(new THREE.Vector3())

        const currentSpherical = new THREE.Spherical()
        currentSpherical.setFromVector3(currentCamPos.clone().sub(tarWorld))
        const targetSpherical = new THREE.Spherical()
        targetSpherical.setFromVector3(posWorld.clone().sub(tarWorld))
        let azimuthDiff = targetSpherical.theta - currentSpherical.theta
        while (azimuthDiff > Math.PI) azimuthDiff -= Math.PI * 2
        while (azimuthDiff < -Math.PI) azimuthDiff += Math.PI * 2
        const adjustedSpherical = new THREE.Spherical(
            targetSpherical.radius,
            targetSpherical.phi,
            currentSpherical.theta + azimuthDiff
        )
        const adjustedPos = new THREE.Vector3().setFromSpherical(adjustedSpherical).add(tarWorld)

        controls.enabled = false
        startPosRef.current.copy(currentCamPos)
        startTarRef.current.copy(currentTar)
        targetPosRef.current.copy(adjustedPos)
        targetTarRef.current.copy(tarWorld)
        progressRef.current = 0
        animatingRef.current = true
    }

    // Initialize default and cinematic camera positions.
    // Uses the active preset's default/cinematic entries when available,
    // falling back to the store values for tiers that don't define them.
    // Also applies the position immediately to the camera controls so the
    // per-device default is visible on load (not just used as a return-to target).
    useEffect(() => {
        const preset = getCameraPreset(deviceType, deviceOrientation)

        // 1. Always update default + cinematic refs for the new preset tier
        const resolvedDefault = resolve(preset.default)
        const defPos = resolvedDefault ? resolvedDefault.posWorld.toArray() : defaultCameraPos
        const defTar = resolvedDefault ? resolvedDefault.tarWorld.toArray() : defaultCameraTar
        initialPositionRef.current.set(defPos[0], defPos[1], defPos[2])
        initialTargetRef.current.set(defTar[0], defTar[1], defTar[2])

        const resolvedCinematic = resolve(preset.cinematic)
        const cinPos = resolvedCinematic ? resolvedCinematic.posWorld.toArray() : cinematicCameraPos
        const cinTar = resolvedCinematic ? resolvedCinematic.tarWorld.toArray() : cinematicCameraTar
        cinematicPositionRef.current.set(cinPos[0], cinPos[1], cinPos[2])
        cinematicTargetRef.current.set(cinTar[0], cinTar[1], cinTar[2])

        if (!cameraControlsRef.current) return

        // 2. Decide where the camera should go based on current view state.
        //    Read state directly (not as a subscription) so this effect doesn't
        //    re-run on every SEC change — only on preset/orientation changes.
        const { viewState, selectedSec } = useGame.getState()

        if (viewState === 'default_state') {
            // No active section — snap to the new preset's default position
            cameraControlsRef.current.setLookAt(
                defPos[0], defPos[1], defPos[2],
                defTar[0], defTar[1], defTar[2],
                false
            )
        } else if (selectedSec) {
            // Zoomed into a SEC — re-resolve and animate to that SEC's position
            // in the new orientation's preset so the framing stays correct
            const entry = getSecEntry(preset, selectedSec)
            const resolved = resolve(entry)
            if (resolved) {
                animateToTarget(resolved.posWorld, resolved.tarWorld)
            }
        }
    }, [defaultCameraPos, defaultCameraTar, cinematicCameraPos, cinematicCameraTar, deviceType, deviceOrientation, scene])

    // Manual animation loop
    useFrame((state, delta) => {
        if (!animatingRef.current || !cameraControlsRef.current) return
        
        progressRef.current += delta / durationRef.current
        
        if (progressRef.current >= 1.0) {
            // Animation complete
            progressRef.current = 1.0
            animatingRef.current = false
            // Re-enable controls when animation finishes
            // BUT: don't re-enable if we're in cinematic mode or SEC006 (weapon stores)
            const cinematicActive = useGame.getState().cinematicMode
            const sec006Active = useGame.getState().selectedSec === 'SEC006'
            if (!cinematicActive && !sec006Active) {
                cameraControlsRef.current.enabled = true
            }
            // If this was the inbound cinematic move, fire the weapon sequence now
            // that the camera has actually reached its target position.
            if (cinematicInboundRef.current) {
                cinematicInboundRef.current = false
                window.dispatchEvent(new CustomEvent('cinematic-camera-ready'))
            }
        }
        
        // Smooth easing (ease-in-out)
        const t = progressRef.current
        const eased = t < 0.5 
            ? 2 * t * t 
            : 1 - Math.pow(-2 * t + 2, 2) / 2
        
        // Interpolate position and target
        const newPos = new THREE.Vector3().lerpVectors(startPosRef.current, targetPosRef.current, eased)
        const newTar = new THREE.Vector3().lerpVectors(startTarRef.current, targetTarRef.current, eased)
        
        // Apply to camera controls
        cameraControlsRef.current.setLookAt(
            newPos.x, newPos.y, newPos.z,
            newTar.x, newTar.y, newTar.z,
            false // no transition, we're doing it manually
        )
    })

    // Handle SEC selection events
    useEffect(() => {
        const handleSecSelected = (event) => {
            const { secData } = event.detail || {}
            if (!secData || !cameraControlsRef.current) return

            const preset = getCameraPreset(
                useGame.getState().deviceType,
                useGame.getState().deviceOrientation
            )
            const entry = getSecEntry(preset, secData.id)
                ?? { posNode: secData.position, tarNode: secData.target }

            const resolved = resolve(entry)
            if (!resolved) return

            let { posWorld, tarWorld } = resolved

            // SEC006: per-tier X offset nudges camera left so the weapon bay
            // is centred on the available screen area on narrow viewports.
            if (secData.id === 'SEC006') {
                if (window.matchMedia(TABLET_PORTRAIT).matches) {
                    posWorld = posWorld.clone()
                    tarWorld = tarWorld.clone()
                    posWorld.x += SEC006_X_OFFSET_TABLET_PORTRAIT
                    tarWorld.x += SEC006_X_OFFSET_TABLET_PORTRAIT
                } else if (window.matchMedia(MOBILE_PORTRAIT).matches) {
                    posWorld = posWorld.clone()
                    tarWorld = tarWorld.clone()
                    posWorld.x += SEC006_X_OFFSET_MOBILE_PORTRAIT
                    tarWorld.x += SEC006_X_OFFSET_MOBILE_PORTRAIT
                }
            }

            animateToTarget(posWorld, tarWorld)
        }

        // sec-camera-move fires when the window re-opens an already-selected SEC;
        // the camera move logic is identical to sec-selected.
        const handleSecCameraMove = (event) => handleSecSelected(event)

        const handleSecDeselected = () => {
            if (!cameraControlsRef.current) return
            
            const controls = cameraControlsRef.current
            const currentCamPos = controls.getPosition(new THREE.Vector3())
            const currentTar = controls.getTarget(new THREE.Vector3())
            
            // Disable controls during animation
            controls.enabled = false
            
            // Set up manual animation back to default
            startPosRef.current.copy(currentCamPos)
            startTarRef.current.copy(currentTar)
            targetPosRef.current.copy(initialPositionRef.current)
            targetTarRef.current.copy(initialTargetRef.current)
            progressRef.current = 0
            animatingRef.current = true
        }

        const handleSecSubsectionBack = (event) => {
            if (!cameraControlsRef.current) return
            const { returnToSec } = event.detail || {}
            if (!returnToSec) return

            const preset = getCameraPreset(
                useGame.getState().deviceType,
                useGame.getState().deviceOrientation
            )
            const entry = getSecEntry(preset, returnToSec)
            const resolved = resolve(entry)
            if (!resolved) return

            animateToTarget(resolved.posWorld, resolved.tarWorld)
        }
        
        window.addEventListener('sec-selected', handleSecSelected)
        window.addEventListener('sec-camera-move', handleSecCameraMove)
        window.addEventListener('sec-deselected', handleSecDeselected)
        window.addEventListener('sec-subsection-back', handleSecSubsectionBack)
        
        return () => {
            window.removeEventListener('sec-selected', handleSecSelected)
            window.removeEventListener('sec-camera-move', handleSecCameraMove)
            window.removeEventListener('sec-deselected', handleSecDeselected)
            window.removeEventListener('sec-subsection-back', handleSecSubsectionBack)
        }
    }, [scene, cameraControlsRef])

    // Handle POI back-to-subsection: return camera to the parent SEC position
    useEffect(() => {
        const handlePoiBackToSubsection = (event) => {
            if (!cameraControlsRef.current) return
            const { returnToSec } = event.detail || {}
            if (!returnToSec) return

            const preset = getCameraPreset(
                useGame.getState().deviceType,
                useGame.getState().deviceOrientation
            )
            const entry = getSecEntry(preset, returnToSec)
            const resolved = resolve(entry)
            if (!resolved) return

            animateToTarget(resolved.posWorld, resolved.tarWorld)
        }

        window.addEventListener('poi-back-to-subsection', handlePoiBackToSubsection)
        return () => {
            window.removeEventListener('poi-back-to-subsection', handlePoiBackToSubsection)
        }
    }, [scene, cameraControlsRef])

    // Handle cinematic mode
    useEffect(() => {
        // Track all timeout IDs so we can cancel them on unmount
        const timeoutIds = []

        const handleCinematicMode = () => {
            if (!cameraControlsRef.current) return

            const controls = cameraControlsRef.current
            const currentCamPos = controls.getPosition(new THREE.Vector3())
            const currentTar = controls.getTarget(new THREE.Vector3())

            // Set cinematic mode to true (hides SEC markers and release weapon button)
            useGame.getState().setCinematicMode(true)

            // Disable controls during animation
            controls.enabled = false

            // Animate to cinematic position
            startPosRef.current.copy(currentCamPos)
            startTarRef.current.copy(currentTar)
            targetPosRef.current.copy(cinematicPositionRef.current)
            targetTarRef.current.copy(cinematicTargetRef.current)
            progressRef.current = 0
            animatingRef.current = true
            // Mark as inbound cinematic move — useFrame will dispatch
            // 'cinematic-camera-ready' the exact frame the camera arrives.
            cinematicInboundRef.current = true

            // After 7 seconds at cinematic position, animate back to default
            timeoutIds.push(setTimeout(() => {
                if (!cameraControlsRef.current) return
                
                const cinematicPos = controls.getPosition(new THREE.Vector3())
                const cinematicTar = controls.getTarget(new THREE.Vector3())
                
                controls.enabled = false
                
                startPosRef.current.copy(cinematicPos)
                startTarRef.current.copy(cinematicTar)
                targetPosRef.current.copy(initialPositionRef.current)
                targetTarRef.current.copy(initialTargetRef.current)
                progressRef.current = 0
                animatingRef.current = true
                
                // Re-enable controls and exit cinematic mode after return animation (2 seconds)
                timeoutIds.push(setTimeout(() => {
                    useGame.getState().setCinematicMode(false)
                    if (cameraControlsRef.current) {
                        cameraControlsRef.current.enabled = true
                    }
                }, 2000))
            }, 7000))
        }

        window.addEventListener('cinematic-mode', handleCinematicMode)
        return () => {
            window.removeEventListener('cinematic-mode', handleCinematicMode)
            // Cancel all pending timeouts so cinematicMode is never left stuck as true
            timeoutIds.forEach(id => clearTimeout(id))
            cinematicInboundRef.current = false
            useGame.getState().setCinematicMode(false)
        }
    }, [cameraControlsRef])

    return null
}
